// Centralized delivery / production lead-time estimation.
//
// Each product can carry a per-product override (production_days_min / production_days_max).
// When both are set, they replace the default country-based estimate for that product.
// Otherwise we fall back to the hardcoded per-country defaults (personalized items take longer).
//
// For a cart/order with multiple items, the displayed estimate is the LONGEST lead time
// among all items (the item that takes the most days to make/deliver).

import { getCustomOptionGroups } from './customOptionUtils';

const monthFormatter = (date) => {
  const day = date.getDate();
  const month = date.toLocaleDateString('sr-Latn-RS', { month: 'long' });
  const year = date.getFullYear();
  return `${day}. ${month} ${year}`;
};

// Resolve an option's productionDays config against a baseline lead time.
// A country-specific entry (byCountry.BA = { min, max }) beats the all-countries setting.
// Two all-countries modes:
//   - override: { mode:'override', min, max }  → absolute range (legacy { min, max } too)
//   - add:      { mode:'add', days }           → baseline + days on both ends
// Returns { startDays, endDays } or null when the option carries no valid override.
function resolveOptionLead(option, baseline, country) {
  const pd = option?.productionDays;
  if (!pd) return null;

  const forCountry = country ? pd.byCountry?.[country] : null;
  if (forCountry && forCountry.min != null && forCountry.max != null) {
    return { startDays: Number(forCountry.min), endDays: Number(forCountry.max) };
  }

  const mode = pd.mode || ((pd.min != null && pd.max != null) ? 'override' : null);
  if (mode === 'add' && pd.days != null) {
    const d = Number(pd.days);
    return { startDays: baseline.startDays + d, endDays: baseline.endDays + d };
  }
  if (mode === 'override' && pd.min != null && pd.max != null) {
    return { startDays: Number(pd.min), endDays: Number(pd.max) };
  }
  return null;
}

// The badge text to show next to an option, e.g. "Dostupno odmah".
// A country-specific badge beats the all-countries one. Returns null when there is none.
export function getOptionBadge(option, country) {
  const pd = option?.productionDays;
  if (!pd) return null;
  const forCountry = country ? pd.byCountry?.[country] : null;
  return forCountry?.badge || pd.badge || null;
}

// Returns { startDays, endDays } from the selected custom option(s), resolved against
// the product baseline, or null when no selected option carries an override.
export function getSelectedOptionLeadDays(customOptions, serializedCustomOption, baseline, country) {
  if (!serializedCustomOption || !customOptions || !baseline) return null;
  const groups = getCustomOptionGroups(customOptions);
  if (groups.length === 0) return null;

  let selections = null;
  try {
    const parsed = JSON.parse(serializedCustomOption);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) selections = parsed;
  } catch {}

  let best = null;
  groups.forEach((group) => {
    const opts = group.options || [];
    const selectedLabel = selections ? selections[group.customHandle] : serializedCustomOption;
    const opt = opts.find((o) => o.label === selectedLabel);
    const lead = resolveOptionLead(opt, baseline, country);
    if (lead && (!best || lead.endDays > best.endDays)) best = lead;
  });
  return best;
}

// Returns { startDays, endDays } for a single product object.
// A per-country override beats the all-countries one, which beats the hardcoded defaults.
export function getProductLeadDays(product, apiCountry) {
  const forCountry = apiCountry ? product?.production_days_by_country?.[apiCountry] : null;
  if (forCountry && forCountry.min != null && forCountry.max != null) {
    return { startDays: Number(forCountry.min), endDays: Number(forCountry.max) };
  }

  const min = product?.production_days_min;
  const max = product?.production_days_max;
  if (min != null && max != null) {
    return { startDays: Number(min), endDays: Number(max) };
  }

  const path = (product?.path || '').toLowerCase();
  const isPersonal = path.includes('personalna-ljubimac-soljica') || path.includes('personalni-tanjir');

  if (isPersonal) {
    return (apiCountry === 'HR' || apiCountry === 'RS')
      ? { startDays: 35, endDays: 42 }
      : { startDays: 28, endDays: 35 };
  }
  if (apiCountry === 'HR' || apiCountry === 'RS') return { startDays: 20, endDays: 30 };
  if (apiCountry === 'BA') return { startDays: 7, endDays: 20 };
  return { startDays: 20, endDays: 30 };
}

// How an option's productionDays setting compares to the product/country baseline.
// Returns { optMin, optMax, deltaMin, deltaMax } or null when the option has no override.
// Positive delta = selecting the option makes delivery take longer.
export function getOptionExtraDays(product, apiCountry, option) {
  const baseline = getProductLeadDays(product, apiCountry);
  const lead = resolveOptionLead(option, baseline, apiCountry);
  if (!lead) return null;
  return {
    optMin: lead.startDays,
    optMax: lead.endDays,
    deltaMin: lead.startDays - baseline.startDays,
    deltaMax: lead.endDays - baseline.endDays,
  };
}

// Lead time for a single cart/product-page item, applying precedence:
// selected option override/add → product override → country default.
export function getItemLeadDays(product, apiCountry, serializedCustomOption) {
  const baseline = getProductLeadDays(product, apiCountry);
  const optionLead = getSelectedOptionLeadDays(product?.custom_options, serializedCustomOption, baseline, apiCountry);
  return optionLead || baseline;
}

// Picks the longest lead time across all cart items.
// cartItems: array with { id, customOption }, productsData: array of product objects.
export function getCartLeadDays(cartItems, productsData, apiCountry) {
  let best = null;
  (cartItems || []).forEach((item) => {
    const p = (productsData || []).find((pd) => pd.id === item.id);
    if (!p) return;
    const lead = getItemLeadDays(p, apiCountry, item.customOption);
    if (!best || lead.endDays > best.endDays) best = lead;
  });
  return best || getProductLeadDays(null, apiCountry);
}

// Picks the longest lead time across already-snapshotted order items.
// Each item is expected to expose production_days_min/max + path/nameBA/nameRS
// (e.g. order product_details). `getFields` maps an item to that shape.
export function getOrderLeadDays(items, apiCountry, getFields) {
  let best = null;
  (items || []).forEach((item) => {
    const fields = getFields ? getFields(item) : item;
    const lead = getProductLeadDays(fields, apiCountry);
    if (!best || lead.endDays > best.endDays) best = lead;
  });
  return best || getProductLeadDays(null, apiCountry);
}

// Converts { startDays, endDays } into formatted { start, end } date strings.
export function formatLeadDates(leadDays) {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + leadDays.startDays);
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + leadDays.endDays);
  return { start: monthFormatter(startDate), end: monthFormatter(endDate) };
}

// Formats the order's stored delivery_estimate snapshot (ISO date strings) for display,
// or null when the snapshot is absent (legacy orders).
export function formatStoredDateRange(startISO, endISO) {
  if (!startISO || !endISO) return null;
  const start = new Date(startISO);
  const end = new Date(endISO);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return { start: monthFormatter(start), end: monthFormatter(end) };
}
